import {
  UseMutationResult,
  useMutation as useRQMutation,
  UseMutationOptions,
  MutationFunction,
  MutationKey,
} from 'react-query';
import { AxiosRequestConfig, RawAxiosRequestHeaders } from 'axios';
import { useCallback } from 'react';

import { CaptchaAction } from '@percent/utility';

import { useClient } from '@percent/hosted-validation-form/hooks';
import { useCaptcha } from '@percent/hosted-validation-form/context/captcha/useCaptcha/useCaptcha';
import { isInvalidCaptchaTokenError } from '@percent/hosted-validation-form/utils/isInvalidCaptchaTokenError';

import { MutationFn } from './useMutation.types';

const CAPTCHA_TOKEN_HEADER = 'recaptcha-token';

export const useMutation = <TData = unknown, TError = unknown, TParams = unknown, TContext = unknown, TBody = TParams>(
  mutationKey: MutationKey,
  mutation: MutationFn<TParams, TData, TError, TBody>,
  options?: UseMutationOptions<TData, TError, TParams, TContext> & {
    isFileUpload?: boolean;
    captchaAction?: CaptchaAction;
  },
): UseMutationResult<TData, TError, TParams, TContext> => {
  const client = useClient();
  const { getToken: getCaptchaToken, reset: resetCaptcha } = useCaptcha();

  const isFileUpload = options?.isFileUpload;
  const captchaAction = options?.captchaAction;

  const mutationFn: MutationFunction<TData, TParams> = useCallback(
    async (variables) => {
      const { endpoint, params, method } = mutation(variables);

      const captchaToken = captchaAction ? await getCaptchaToken(captchaAction) : undefined;

      const headers: RawAxiosRequestHeaders = {
        ...(isFileUpload && { 'Content-Type': 'multipart/form-data' }),
        ...(captchaToken && { [CAPTCHA_TOKEN_HEADER]: captchaToken }),
      };

      const body = isFileUpload ? params : JSON.stringify(params);

      const axiosConfig: AxiosRequestConfig = {
        url: endpoint,
        data: params ? body : undefined,
        method: method || 'POST',
        ...(Object.keys(headers).length > 0 && { headers }),
      };

      try {
        return await client.request(axiosConfig);
      } catch (error) {
        if (captchaAction && isInvalidCaptchaTokenError(error)) {
          resetCaptcha();
        }

        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, mutation, isFileUpload, captchaAction, getCaptchaToken, resetCaptcha],
  );

  return useRQMutation<TData, TError, TParams, TContext>(mutationKey, mutationFn, options);
};
